import { Add } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useNavigate } from 'react-router';

import { createProjectFestival } from '@/domain/project-festival/actions/create-project-festival.action';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import {
  checkUserCreateFestival,
  selectIsCreatingProject,
} from '@/domain/project-festival/project-festival.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { ProjectFestivalForm } from '../../components/ProjectFestivalForm';

const initialProjectState: ProjectFestival = {
  id: -1,
  type: 'festival',
  image: '',
  name: '',
  startDate: '',
  endDate: '',
  location: '',
  venue: 'ON_PREMISES',
  avgCapacity: 0,
  accessToEnergy: false,
  accessToWater: false,
  calculateOptions: {
    includePublicAnswersForCo2: true,
  },
  models: { ess: 'ESS_1', env: 'ENV_1' }, // TODO could we remove these data here (models) and other field that cannot be updated by the form
};

export function ButtonModalCreateProjectFestival() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isCreatingProject = useAppSelector(selectIsCreatingProject);
  const userCanCreateFestival = useAppSelector(checkUserCreateFestival);

  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);
  const [project, setProject] =
    React.useState<ProjectFestival>(initialProjectState);

  const resetDatas = () => {
    setStep(1);
    setProject({
      ...initialProjectState,
    });
  };

  const handleCreateProject = async (data: ProjectFestival) => {
    try {
      const params = { project: data };
      await dispatch(createProjectFestival(params)).unwrap();
      resetDatas();
      setOpen(false);
      handleClose();
      navigate('/app/projects/festival');
    } catch (error) {
      console.error(error);
    }
  };

  const handleNextPage = () => {
    setStep(2);
  };

  const handleCancel = () => {
    resetDatas();
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    resetModalContent();
  };

  const resetModalContent = () => {
    setStep(1);
    setProject({
      ...initialProjectState,
    });
  };

  const handleOpen = () => {
    if (userCanCreateFestival) {
      setOpen(true);
    } else {
      enqueueSnackbar(translate('License.MaxEvent'), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        action: (
          <FSSecondaryButton onClick={() => navigate('/app/contact')}>
            {translate('License.MaxEventAction')}
          </FSSecondaryButton>
        ),
      });
    }
  };

  return (
    <>
      <FSPrimaryButton startIcon={<Add />} onClick={handleOpen}>
        {translate('ProjectsFestival.new-project-button')}
      </FSPrimaryButton>
      <FSModal open={open} onClose={handleClose}>
        <Stack spacing={3}>
          <FSModalTitle
            title={`${translate('ProjectsFestival.create-your-event')} ${step}/2`}
          />
          <ProjectFestivalForm
            project={project}
            type="create"
            step={step}
            buttons={
              <FormButtons
                step={step}
                setStep={setStep}
                handleCancel={handleCancel}
                isCreatingProject={isCreatingProject}
              />
            }
            onSubmit={step === 1 ? handleNextPage : handleCreateProject}
          />
        </Stack>
      </FSModal>
    </>
  );
}

function FormButtons({
  step,
  setStep,
  handleCancel,
  isCreatingProject,
}: {
  step: number;
  setStep: (step: number) => void;
  handleCancel: () => void;
  isCreatingProject: boolean;
}) {
  return (
    <Stack
      display={'flex'}
      direction={'row'}
      justifyContent={'end'}
      spacing={1}
      sx={{ mt: 2 }}
    >
      {step === 1 ? (
        // BUTTONS STEP 1
        <>
          <FSSecondaryButton onClick={handleCancel}>
            {translate('Form.cancel')}
          </FSSecondaryButton>
          <FSPrimaryButton type="submit">
            {translate('Form.next')}
          </FSPrimaryButton>
        </>
      ) : (
        //  BUTTONS STEP 2
        <>
          <FSSecondaryButton onClick={() => setStep(1)}>
            {translate('Form.back')}
          </FSSecondaryButton>
          <FSPrimaryButton disabled={isCreatingProject} type="submit">
            {translate('Form.create')}
          </FSPrimaryButton>
        </>
      )}
    </Stack>
  );
}
